import { BarChart3, RefreshCw } from 'lucide-react'
import { useAiUsageStats } from '../../src/hooks/data/useAiUsage'

const FEATURE_LABELS: Record<string, string> = {
  chat_requests: 'Chat (storefront + suggest-reply)',
  commerce_requests: 'Commerce AI (Q&A / recs / descriptions)',
  agent_requests: 'Agents (shopping / analytics / support)',
  automation_requests: 'Automation (low-stock / cart recovery / reviews / re-engagement)',
  mcp_tool_calls: 'MCP tool calls',
  ab_assignments: 'A/B test assignments (chat/agent)',
}

/** Real per-feature counts from ai-engine's own Prometheus counters
 * (ai-engine/app/routers/verification.py) — not mocked. */
export default function AiUsageWidget() {
  const { data, isLoading, isFetching, refetch } = useAiUsageStats()

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          <h2 className="font-semibold text-gray-800 dark:text-white">AI Usage</h2>
        </div>
        <button onClick={() => refetch()} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
          <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg bg-gray-100 dark:bg-gray-700/40 animate-pulse" />
          ))
        ) : !data || Object.values(data.totals).every((v) => v === 0) ? (
          <p className="text-sm text-gray-500">No AI feature calls recorded yet.</p>
        ) : (
          Object.entries(data.totals).map(([feature, total]) => {
            const breakdown = data.breakdown[feature] ?? {}
            const labels = Object.entries(breakdown).filter(([, v]) => v > 0)
            return (
              <div key={feature} className="rounded-lg border border-gray-100 dark:border-gray-700 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {FEATURE_LABELS[feature] ?? feature}
                  </span>
                  <span className="text-lg font-bold text-gray-900 dark:text-white">{total}</span>
                </div>
                {labels.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {labels.map(([label, count]) => (
                      <span
                        key={label}
                        className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                      >
                        {label}: {count}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
